import React, { useEffect, useState } from "react";
import "./filter.scss";

interface FilterProps {
  brands: string[];
  usageSet: string[];
  typeSet: string[];
  sale: boolean;
  notSale: boolean;
  club: boolean;
  showClubFilter?: boolean;
  onChange: (value: any) => any;
  clearTrigger?: number;
  getTypeCount?: (type: string) => number;
  getUsageCount?: (usage: string) => number;
  getBrandCount?: (brand: string) => number;
  getSaleCount?: (sale: boolean) => number;
  getNotSaleCount?: (notSale: boolean) => number;
  getClubCount?: () => number;
}

function ProductsFilter(props: FilterProps) {
  const [selectedBrands, setSelectedBrands] = useState<Set<string>>(new Set());
  const [selectedUsages, setSelectedUsages] = useState<Set<string>>(new Set());
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const [expandedFilters, setExpandedFilters] = useState<Set<string>>(new Set(['sale', 'type', 'usage', 'brand']));
  const [showAllFilters, setShowAllFilters] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedBrands(new Set());
    setSelectedUsages(new Set());
    setSelectedTypes(new Set());

    props.onChange({
      brands: new Set(),
      usages: new Set(),
      types: new Set(),
      sale: false,
      notSale: false,
      club: false,
    });
  }, [props.clearTrigger]);

  const onSaleChange = (e: any) => {
    if (props.notSale) props.onChange({ notSale: false });
    props.onChange({ sale: e.target.checked });
  };

  const onNotSaleChange = (e: any) => {
    if (props.sale) props.onChange({ sale: false });
    props.onChange({ notSale: e.target.checked });
  };

  const onClubChange = (e: any) => {
    props.onChange({ club: e.target.checked });
  };

  const handleCheckboxChange = (
    value: string,
    set: Set<string>,
    setState: (newSet: Set<string>) => void,
    type: "brands" | "usages" | "types"
  ) => {
    const newSet = new Set(set);
    if (newSet.has(value)) {
      newSet.delete(value);
    } else {
      newSet.add(value);
    }
    setState(newSet);
    props.onChange({ [type]: newSet });

    window.scrollTo(0,0)
  };

  const toggleFilter = (filterType: string) => {
    const newExpandedFilters = new Set(expandedFilters);
    if (newExpandedFilters.has(filterType)) {
      newExpandedFilters.delete(filterType);
    } else {
      newExpandedFilters.add(filterType);
    }
    setExpandedFilters(newExpandedFilters);
  };

  const toggleShowAll = (filterType: string) => {
    const newShowAllFilters = new Set(showAllFilters);
    if (newShowAllFilters.has(filterType)) {
      newShowAllFilters.delete(filterType);
    } else {
      newShowAllFilters.add(filterType);
    }
    setShowAllFilters(newShowAllFilters);
  };

  const getSortedAndLimitedFilters = (
    items: string[],
    getCount: ((item: string) => number) | undefined,
    filterType: string
  ) => {
    let sorted: string[];
    
    if (getCount) {
      // Sort by count (descending)
      sorted = [...items].sort((a, b) => {
        const countA = getCount(a) || 0;
        const countB = getCount(b) || 0;
        return countB - countA;
      });
    } else {
      // If no count function, just use items as-is
      sorted = [...items];
    }

    // If showing all, return all sorted items
    if (showAllFilters.has(filterType)) {
      return sorted;
    }

    // Otherwise, return top 15
    return sorted.slice(0, 15);
  };

  return (
    <div className='products-filter-container'>
      <div className='sale-filter filter'>
        <div className='filter-title' onClick={() => toggleFilter('sale')}>
          Хямдрал
          <span className='filter-toggle'>{expandedFilters.has('sale') ? '−' : '+'}</span>
        </div>
        {expandedFilters.has('sale') && (
          <div className='filter-body'>
          <div className='custom-checkbox'>
            <input
              type='checkbox'
              className='checkbox'
              id='sale-filter-input'
              onChange={onSaleChange}
              checked={props.sale}
            />
            <label htmlFor='sale-filter-input'>
              Хямдарсан{" "}
              <span className='filter-count'>
                ({props.getSaleCount?.(true)})
              </span>
            </label>
          </div>
          <div className='custom-checkbox'>
            <input
              type='checkbox'
              className='checkbox'
              id='notSale-filter-input'
              onChange={onNotSaleChange}
              checked={props.notSale}
            />
            <label htmlFor='notSale-filter-input'>
              Хямдараагүй{" "}
              <span className='filter-count'>
                ({props.getNotSaleCount?.(true)})
              </span>
            </label>
          </div>
          {props.showClubFilter && (
            <div className='custom-checkbox'>
              <input
                type='checkbox'
                className='checkbox'
                id='club-filter-input'
                onChange={onClubChange}
                checked={props.club}
              />
              <label htmlFor='club-filter-input'>
                Клуб үнэ{" "}
                <span className='filter-count'>
                  ({props.getClubCount?.()})
                </span>
              </label>
            </div>
          )}
          </div>
        )}
      </div>

      <div className='type-filter filter'>
        <div className='filter-title' onClick={() => toggleFilter('type')}>
          Төрөл
          <span className='filter-toggle'>{expandedFilters.has('type') ? '−' : '+'}</span>
        </div>
        {expandedFilters.has('type') && (
          <div className='filter-body'>
          {getSortedAndLimitedFilters(props.typeSet, props.getTypeCount, 'type').map((type, i) => (
            <div className='custom-checkbox' key={`type-${type}-${i}`}>
              <input
                type='checkbox'
                className='checkbox'
                id={`type-${i}`}
                onChange={() =>
                  handleCheckboxChange(
                    type,
                    selectedTypes,
                    setSelectedTypes,
                    "types"
                  )
                }
                checked={selectedTypes.has(type)}
              />
              <label htmlFor={`type-${i}`}>
                {type}
                <span className='filter-count'>
                  ({props.getTypeCount?.(type)})
                </span>
              </label>
            </div>
          ))}
          {props.typeSet.length > 15 && (
            <button 
              className='show-more-filters-btn'
              onClick={() => toggleShowAll('type')}
            >
              {showAllFilters.has('type') ? 'Цөөн харуулах' : 'Бүгдийг харуулах'}
            </button>
          )}
          </div>
        )}
      </div>

      <div className='usage-filter filter'>
        <div className='filter-title' onClick={() => toggleFilter('usage')}>
          Хэрэглээ
          <span className='filter-toggle'>{expandedFilters.has('usage') ? '−' : '+'}</span>
        </div>
        {expandedFilters.has('usage') && (
          <div className='filter-body'>
          {getSortedAndLimitedFilters(props.usageSet, props.getUsageCount, 'usage').map((usage, i) => (
            <div className='custom-checkbox' key={`usage-${usage}-${i}`}>
              <input
                type='checkbox'
                className='checkbox'
                id={`usage-${i}`}
                onChange={() =>
                  handleCheckboxChange(
                    usage,
                    selectedUsages,
                    setSelectedUsages,
                    "usages"
                  )
                }
                checked={selectedUsages.has(usage)}
              />
              <label htmlFor={`usage-${i}`}>
                {usage}{" "}
                <span className='filter-count'>
                  ({props.getUsageCount?.(usage)})
                </span>
              </label>
            </div>
          ))}
          {props.usageSet.length > 15 && (
            <button 
              className='show-more-filters-btn'
              onClick={() => toggleShowAll('usage')}
            >
              {showAllFilters.has('usage') ? 'Цөөн харуулах' : 'Бүгдийг харуулах'}
            </button>
          )}
          </div>
        )}
      </div>

      <div className='brand-filter filter'>
        <div className='filter-title' onClick={() => toggleFilter('brand')}>
          Брэнд
          <span className='filter-toggle'>{expandedFilters.has('brand') ? '−' : '+'}</span>
        </div>
        {expandedFilters.has('brand') && (
          <div className='filter-body'>
          {getSortedAndLimitedFilters(props.brands, props.getBrandCount, 'brand').map((brand, i) => (
            <div className='custom-checkbox' key={`brand-${brand}-${i}`}>
              <input
                type='checkbox'
                className='checkbox'
                id={`brand-${i}`}
                onChange={() =>
                  handleCheckboxChange(
                    brand,
                    selectedBrands,
                    setSelectedBrands,
                    "brands"
                  )
                }
                checked={selectedBrands.has(brand)}
              />
              <label htmlFor={`brand-${i}`}>
                {brand}
                <span className='filter-count'>
                  ({props.getBrandCount?.(brand)})
                </span>
              </label>
            </div>
          ))}
          {props.brands.length > 15 && (
            <button 
              className='show-more-filters-btn'
              onClick={() => toggleShowAll('brand')}
            >
              {showAllFilters.has('brand') ? 'Цөөн харуулах' : 'Бүгдийг харуулах'}
            </button>
          )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductsFilter;
